import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString, MultiLineString } from "geojson";

import { ensurePreviewLayer, findHitsAlongSegment, removePreviewLayer, setPreviewFeatures } from "./snapPreview";

const { constants: Constants } = MapboxDraw;

const PREVIEW_SOURCE_ID = "snap-add-preview-source";
const PREVIEW_LAYER_ID = "snap-add-preview-layer";
const TRAIL_SOURCE_ID = "snap-add-trail-source";
const TRAIL_LAYER_ID = "snap-add-trail-layer";

type PreviewFeature = Feature<LineString | MultiLineString, { sourceUgPathId: string | number }>;

interface SnapAddState {
  dragging: boolean;
  previewed: Map<string | number, PreviewFeature>;
  trail: Array<[number, number]>;
  lastScreenPoint: [number, number] | null;
}

function trailFeatures(coords: Array<[number, number]>): Array<Feature<LineString>> {
  if (coords.length < 2) return [];
  return [
    {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: coords },
    },
  ];
}

/**
 * Adds any new ug_path features that intersect the screen-space segment from the last drag
 * point to the current event point into the in-progress preview map, and re-renders the
 * preview overlay. Using the full segment (not just the endpoint) stops fast mouse movement
 * from skipping features between onDrag ticks.
 */
function collectHitsIntoPreview(this: MapboxDraw.DrawCustomModeThis, state: SnapAddState, event: MapboxDraw.MapMouseEvent): void {
  const { snapSourceLayer, editOptions } = this._ctx.options;
  const editingLayerId = editOptions?.editingLayerId;
  if (!snapSourceLayer || !editingLayerId) return;

  const currentScreenPoint: [number, number] = [event.point.x, event.point.y];
  const hits = findHitsAlongSegment(
    this.map,
    state.lastScreenPoint,
    currentScreenPoint,
    (f) => f.source === snapSourceLayer.source && f.sourceLayer === snapSourceLayer.sourceLayer
  );
  state.lastScreenPoint = currentScreenPoint;
  if (hits.length === 0) return;

  let changed = false;
  for (const hit of hits) {
    const sourceId = (hit.id ?? hit.properties?.id) as string | number | undefined;
    if (sourceId == null) continue;
    if (state.previewed.has(sourceId)) continue;

    const geom = hit.geometry;
    if (geom.type !== "LineString" && geom.type !== "MultiLineString") continue;

    // queryRenderedFeatures may return tile-clipped geometry; this is fine for typical
    // street-segment ug_paths but long paths spanning tiles can be truncated.
    const clonedGeom: LineString | MultiLineString = {
      type: geom.type,
      coordinates: JSON.parse(JSON.stringify(geom.coordinates)),
    };

    state.previewed.set(sourceId, {
      type: "Feature",
      geometry: clonedGeom,
      properties: { sourceUgPathId: sourceId },
    });
    changed = true;
  }

  if (changed) {
    setPreviewFeatures(this.map, PREVIEW_SOURCE_ID, Array.from(state.previewed.values()));
  }
}

/**
 * Commits every previewed geometry as a new draw feature on the active editing layer and fires
 * a draw.create event so the existing DrawHandler pipeline picks them up.
 */
function commitPreview(this: MapboxDraw.DrawCustomModeThis, state: SnapAddState): Feature[] {
  const editingLayerId = this._ctx.options.editOptions?.editingLayerId;
  if (!editingLayerId || state.previewed.size === 0) return [];

  const created: Feature[] = [];
  state.previewed.forEach((preview, sourceId) => {
    const newFeature = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { layerId: editingLayerId, sourceUgPathId: sourceId },
      geometry: preview.geometry,
    });
    this.addFeature(newFeature);
    created.push(newFeature.toGeoJSON() as Feature);
  });
  return created;
}

/**
 * Custom draw mode that "highlights" rendered features in a configured source-layer (e.g. ug_path)
 * while the user holds the left mouse button. On release, one draw feature per highlighted source
 * feature is committed to the active editing layer.
 *
 * A single click behaves the same way — it highlights one feature and commits it on release.
 *
 * Configuration via MapboxDrawOptions:
 * - snapSourceLayer: { source, sourceLayer } identifying the vector tile layer to snap to
 * - editOptions.editingLayerId: which draw layer newly-created features are assigned to
 *
 * Created features carry { layerId, sourceUgPathId } so that snap_delete_feature and subsequent
 * edits can match them back to their source.
 */
const snapAddFeature: DrawCustomMode<SnapAddState> = {
  onSetup(): SnapAddState {
    this.updateUIClasses({ mouse: "add" });
    ensurePreviewLayer(this.map, PREVIEW_SOURCE_ID, PREVIEW_LAYER_ID, {
      "line-color": "#00c853",
      "line-width": 6,
      "line-opacity": 0.6,
    });
    ensurePreviewLayer(this.map, TRAIL_SOURCE_ID, TRAIL_LAYER_ID, {
      "line-color": "#00c853",
      "line-width": 2,
      "line-opacity": 0.9,
      "line-dasharray": [1, 1],
    });
    return {
      dragging: false,
      previewed: new Map(),
      trail: [],
      lastScreenPoint: null,
    };
  },

  onMouseDown(state: SnapAddState, event: MapboxDraw.MapMouseEvent) {
    // Only react to primary (left) button — let middle/right pass through so the user can still
    // pan and context-click the map while the mode is active.
    if (event.originalEvent.button !== 0) return;

    state.dragging = true;
    state.previewed = new Map();
    state.trail = [[event.lngLat.lng, event.lngLat.lat]];
    state.lastScreenPoint = null;
    this.map.dragPan.disable();
    collectHitsIntoPreview.call(this, state, event);
  },

  onDrag(state: SnapAddState, event: MapboxDraw.MapMouseEvent) {
    if (!state.dragging) return;
    state.trail.push([event.lngLat.lng, event.lngLat.lat]);
    setPreviewFeatures(this.map, TRAIL_SOURCE_ID, trailFeatures(state.trail));
    collectHitsIntoPreview.call(this, state, event);
  },

  onMouseUp(state: SnapAddState) {
    if (!state.dragging) return;
    state.dragging = false;
    this.map.dragPan.enable();
    const created = commitPreview.call(this, state);
    state.previewed = new Map();
    state.trail = [];
    state.lastScreenPoint = null;
    setPreviewFeatures(this.map, PREVIEW_SOURCE_ID, []);
    setPreviewFeatures(this.map, TRAIL_SOURCE_ID, []);
    if (created.length > 0) {
      this.map.fire(Constants.events.CREATE, { features: created });
    }
  },

  onStop() {
    this.map.dragPan.enable();
    removePreviewLayer(this.map, PREVIEW_SOURCE_ID, PREVIEW_LAYER_ID);
    removePreviewLayer(this.map, TRAIL_SOURCE_ID, TRAIL_LAYER_ID);
  },

  toDisplayFeatures(_state, geojson, display) {
    display(geojson);
  },
};

export default snapAddFeature;
